
/**
 * This utility contains functions to help refresh metadata caches
 * and ensure social platforms pick up the correct metadata
 */

import { updateAllMetadata, refreshFacebookCache } from './dynamicMetadata';
import { APP_VERSION } from './versionManager';

// Track when we last attempted to refresh metadata
let lastMetadataRefresh: number = 0;
const REFRESH_INTERVAL = 60000; // 60 seconds in milliseconds

// The domain of our application
const APP_DOMAIN = "https://selahtools.com";

/**
 * Force all metadata tags to be refreshed with current values
 */
export const refreshMetadata = () => {
  const now = Date.now();
  
  // Don't refresh too frequently to avoid unnecessary operations
  if (now - lastMetadataRefresh < REFRESH_INTERVAL) {
    return false;
  }
  
  try {
    // Use our unified metadata system to update all tags
    updateAllMetadata({});
    
    // Try to refresh Facebook's cache if the API is available
    try {
      refreshFacebookCache();
    } catch (fbError) {
      console.error('Facebook cache refresh failed:', fbError);
    }
    
    // Track when we refreshed
    lastMetadataRefresh = now;
    
    return true;
  } catch (e) {
    console.error('Error refreshing metadata:', e);
    return false;
  }
};

/**
 * Attach event listeners to automatically refresh metadata 
 * when a user attempts to share content
 */
export const initMetadataRefresher = () => {
  // Create a mutation observer to detect DOM changes that might affect sharing
  const observer = typeof MutationObserver !== 'undefined' 
    ? new MutationObserver((mutations) => {
        // Check if any mutations include sharing-related elements
        const shouldRefresh = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1) { // Element node
              const element = node as HTMLElement;
              return element.getAttribute?.('data-share-button') !== null ||
                     element.textContent?.toLowerCase().includes('share');
            }
            return false;
          });
        });
        
        if (shouldRefresh) {
          refreshMetadata();
        }
      })
    : null;
  
  // Refresh on page load
  refreshMetadata();
  
  // Start observing the document
  if (observer) {
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }
  
  // Refresh before any share attempt
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const isShareButton = target.closest('[data-share-button]') !== null || 
                           target.textContent?.toLowerCase().includes('share');
    
    if (isShareButton) {
      refreshMetadata();
    }
  });
  
  return {
    refreshMetadata,
    version: APP_VERSION,
    observer // Now returning the observer
  };
};
